import { Fragment } from 'react';
import { RiCheckLine } from '@remixicon/react';
import { cn } from '@/shared/lib/cn';

export interface StepperProps {
  steps: readonly (string | { label: React.ReactNode; disabled?: boolean })[];
  /** Index of the current step (0-based). Earlier steps are done. */
  current: number;
  /** Makes done steps clickable (go back). */
  onStepClick?: (index: number) => void;
  /** Screen-reader suffixes of done / current steps. */
  stateLabels?: { done: string; current: string };
  'aria-label'?: string;
  className?: string;
}

/**
 * Wizard stepper (516:11317 / 516:11329): row h44, padding 12/16, gap 8. Circle 20: done = `#35b5ec` + check 12,
 * current = `#35b5ec` + number 600 11 white, upcoming = White/10 + number. Label 500 12/auto (done White/60,
 * current / upcoming white). Connector h2 r1 flex-1: `#35b5ec` once the step before it is done, else White/10.
 */
export function Stepper({
  steps,
  current,
  onStepClick,
  stateLabels = { done: 'bajarildi', current: 'joriy qadam' },
  'aria-label': ariaLabel = 'Qadamlar',
  className,
}: StepperProps) {
  return (
    <ol aria-label={ariaLabel} className={cn('flex h-44 items-center gap-8 px-16 py-12', className)}>
      {steps.map((raw, i) => {
        const step = typeof raw === 'string' ? { label: raw } : raw;
        const state = i < current ? 'done' : i === current ? 'current' : 'upcoming';
        const clickable = state === 'done' && onStepClick && !('disabled' in step && step.disabled);
        const content = (
          <>
            <span
              className={cn(
                'flex size-20 shrink-0 items-center justify-center rounded-10 text-11 leading-auto font-semibold text-white',
                state === 'upcoming' ? 'bg-field text-fg' : 'bg-accent-2',
              )}
            >
              {state === 'done' ? <RiCheckLine aria-hidden className="size-12" /> : i + 1}
            </span>
            <span
              className={cn(
                'truncate text-xs leading-auto font-medium tracking-normal',
                state === 'done' ? 'text-fg-secondary' : 'text-fg',
              )}
            >
              {step.label}
            </span>
          </>
        );
        return (
          <Fragment key={i}>
            {i > 0 && (
              <li aria-hidden className={cn('h-2 min-w-16 flex-1 rounded-1', i <= current ? 'bg-accent-2' : 'bg-field')} />
            )}
            <li className="flex min-w-0 shrink-0 items-center" aria-current={state === 'current' ? 'step' : undefined}>
              {clickable ? (
                <button
                  type="button"
                  onClick={() => onStepClick(i)}
                  className="flex min-w-0 cursor-pointer items-center gap-8 rounded-4 hover:opacity-80"
                >
                  {content}
                </button>
              ) : (
                <span className="flex min-w-0 items-center gap-8">{content}</span>
              )}
              <span className="sr-only">{state === 'done' ? ` (${stateLabels.done})` : state === 'current' ? ` (${stateLabels.current})` : ''}</span>
            </li>
          </Fragment>
        );
      })}
    </ol>
  );
}
